import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useResponsiveMap } from '@/hooks/useResponsiveMap';

interface MapSizeMonitorProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  className?: string;
}

export const MapSizeMonitor: React.FC<MapSizeMonitorProps> = ({ 
  position = 'bottom-left',
  className = ''
}) => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080
  });

  const { containerHeight, layoutSize, zoom } = useResponsiveMap();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 计算地图实际占用的屏幕面积
  const getMapScreenUsage = () => {
    const screenArea = dimensions.width * dimensions.height;
    const layoutSizePercent = parseFloat(layoutSize.replace('%', '')) / 100;
    const mapArea = dimensions.width * containerHeight * layoutSizePercent * layoutSizePercent;
    const screenUsage = (mapArea / screenArea) * 100;
    
    return {
      screenArea,
      mapArea,
      screenUsage,
      containerHeight,
      layoutSizePercent: layoutSizePercent * 100
    };
  };

  const mapStats = getMapScreenUsage();

  // 位置样式
  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4'
  };

  // 根据屏幕使用率确定颜色
  const getUsageColor = (usage: number) => {
    if (usage >= 70) return 'text-green-400'; // 优秀
    if (usage >= 60) return 'text-yellow-400'; // 良好
    if (usage >= 50) return 'text-orange-400'; // 一般
    return 'text-red-400'; // 需要优化
  };

  return (
    <motion.div 
      className={`fixed ${positionClasses[position]} z-40 ${className}`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-black bg-opacity-80 text-white px-3 py-2 rounded-lg text-xs font-mono backdrop-blur-sm">
        <div className="space-y-1">
          {/* 屏幕尺寸 */}
          <div className="flex items-center justify-between">
            <span className="text-gray-300">屏幕:</span>
            <span>{dimensions.width}×{dimensions.height}</span>
          </div>
          
          {/* 容器高度 */}
          <div className="flex items-center justify-between">
            <span className="text-gray-300">容器:</span>
            <span className="text-blue-400">{Math.round(containerHeight)}px (90%)</span>
          </div>
          
          {/* 地图布局 */}
          <div className="flex items-center justify-between">
            <span className="text-gray-300">布局:</span>
            <span className="text-purple-400">{layoutSize}</span>
          </div>
          
          {/* 缩放级别 */}
          <div className="flex items-center justify-between">
            <span className="text-gray-300">缩放:</span>
            <span className="text-cyan-400">{zoom.toFixed(1)}x</span>
          </div>
          
          {/* 屏幕利用率 */}
          <div className="flex items-center justify-between border-t border-gray-600 pt-1">
            <span className="text-gray-300">利用率:</span>
            <span className={`font-bold ${getUsageColor(mapStats.screenUsage)}`}>
              {mapStats.screenUsage.toFixed(1)}%
            </span>
          </div>
          
          {/* 地图面积 */}
          <div className="flex items-center justify-between">
            <span className="text-gray-300">面积:</span>
            <span className="text-green-400">
              {(mapStats.mapArea / 1000000).toFixed(1)}M px²
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
